import { ScaleLinear, ScaleLogarithmic, ScaleTime, scaleTime } from 'd3-scale';
import { isDate, isNumber, isUndefined } from 'lodash-es';

import {
  MinScaleBoundary,
  MinMaxConfig,
  MaxScaleBoundary,
} from '../types/min-max-config.js';
import { MinMax } from '../types/min-max.js';
import { AxisScaleType } from '../types/scales.js';
import { getScale } from './scale.js';

/**
 * If a value is 'auto' or undefined, then is considered default.
 */
const isDefaultConfig = (config?: MinScaleBoundary | MaxScaleBoundary) =>
  isUndefined(config) || config === 'auto';

const getMinOld = (
  dataBoundary: number,
  configBoundary: MinScaleBoundary | undefined,
  scale: ScaleLogarithmic<number, number> | ScaleLinear<number, number>,
) => {
  const zeroBaselineMin = dataBoundary > 0 ? 0 : dataBoundary;

  if (isNumber(configBoundary)) {
    return Number(configBoundary);
  } else if (isDefaultConfig(configBoundary)) {
    return zeroBaselineMin;
  }

  const [niceMin] = scale.domain();

  return niceMin;
};

const getMin = (
  dataBoundary: number,
  configBoundary: MinScaleBoundary | undefined,
  scale:
    | ScaleLogarithmic<number, number>
    | ScaleLinear<number, number>
    | ScaleTime<number, number>,
): number => {
  if (isNumber(configBoundary)) {
    return Number(configBoundary);
  } else if (configBoundary === 'data-min') {
    return dataBoundary;
  } else {
    const [niceMin] = scale.domain();
    return +niceMin;
  }
};
const getMaxOld = (
  dataBoundary: number,
  configBoundary: MaxScaleBoundary | undefined,
  scale: ScaleLogarithmic<number, number> | ScaleLinear<number, number>,
) => {
  const zeroBaselineMax = dataBoundary < 0 ? 0 : dataBoundary;

  if (isNumber(configBoundary)) {
    return Number(configBoundary);
  } else if (isDefaultConfig(configBoundary)) {
    return zeroBaselineMax;
  }

  const [, niceMax] = scale.domain();

  return niceMax;
};
const getMax = (
  dataBoundary: number,
  configBoundary: MaxScaleBoundary | undefined,
  scale:
    | ScaleLogarithmic<number, number>
    | ScaleLinear<number, number>
    | ScaleTime<number, number>,
): number => {
  if (isNumber(configBoundary)) {
    return Number(configBoundary);
  } else if (configBoundary === 'data-max') {
    return dataBoundary;
  } else {
    const [, niceMax] = scale.domain();
    return +niceMax;
  }
};

/**
 * @param dataBoundaries - Boundaries set by input data
 * @param configBoundaries - Boundaries set by config
 * @param scaleType - Scale type to be used for determining nice values in case of need
 * @returns - Min and max values to be used for any scale that should be created using this boundaries and scale type.
 */
export const getMinMaxForScale = (
  dataBoundaries: MinMax,
  configBoundaries: Partial<MinMaxConfig>,
  scaleType: AxisScaleType = 'linear',
): MinMax => {
  const scale = getScale(scaleType, dataBoundaries);

  const min = getMinOld(dataBoundaries.min, configBoundaries?.min, scale);
  const max = getMaxOld(dataBoundaries.max, configBoundaries?.max, scale);

  return { min, max };
};

/**
 * @param dataBoundaries - Boundaries set by input data
 * @param configBoundaries - Boundaries set by config
 * @param scaleType - Scale type to be used for determining nice values in case of need
 * @returns - Min and max values to be used for any scale that should be created using these boundaries;
 */
export const resolveMinMax = (
  dataBoundaries: MinMax,
  configBoundaries: Partial<MinMaxConfig>,
  scaleType: AxisScaleType = 'linear',
): MinMax => {
  const zeroBaselineMin = dataBoundaries.min > 0 ? 0 : dataBoundaries.min;
  const zeroBaselineMax = dataBoundaries.max < 0 ? 0 : dataBoundaries.max;

  const scale = getScale(scaleType, {
    min: zeroBaselineMin,
    max: zeroBaselineMax,
  });

  const min = getMin(dataBoundaries.min, configBoundaries?.min, scale);
  const max = getMax(dataBoundaries.max, configBoundaries?.max, scale);

  return { min, max };
};

const coerceConfigBoundaries = (configBoundaries: {
  min?: 'auto' | 'data-min' | number | Date;
  max?: 'auto' | 'data-max' | number | Date;
}) => {
  let { min, max } = configBoundaries;
  if (isDate(min)) {
    min = min.valueOf();
  }

  if (isDate(max)) {
    max = max.valueOf();
  }

  return { min, max };
};

export const resolveTimeMinMax = (
  dataBoundaries: MinMax,
  configBoundaries: {
    min?: 'auto' | 'data-min' | number | Date;
    max?: 'auto' | 'data-max' | number | Date;
  },
): MinMax => {
  const scale = scaleTime()
    .domain([dataBoundaries.min, dataBoundaries.max])
    .nice();

  const confBoundaries = coerceConfigBoundaries(configBoundaries);

  const min = getMin(dataBoundaries.min, confBoundaries?.min, scale).valueOf();
  const max = getMax(dataBoundaries.max, confBoundaries?.max, scale).valueOf();

  return { min, max };
};
